// What a drop onto another module does (05g-1) — main.js's drop resolution, the pure part:
// merge into a comparison, switch the target's player, or become Similarity's
// overlay; or nothing (the red outline). Over plain descriptions of the two modules, so
// every rule is unit-tested; drop-actions.ts reads them off the page.
//
// Copied exactly, incl. the order the checks run in — each one's reason is noted where it
// isn't obvious. "modA" is always the module being dragged (the source), "modB" the one
// it's dropped onto (the target).

import type { RawPlayerRecord } from "../data/snapshot.js";
import { playerById, type PlayerId } from "../data/players.js";
import { offeredPlayerIds, openingPlayer } from "./module-shell.js";
import type { PickedPlayer } from "../live/picked-players.js";
import type { ModuleDropAction } from "./undo.js";

/** A comparison tops out at 5 players: past that the bars stop reading as separate
 *  shapes, and the CSS only defines 5 marker / legend colours. */
export const MAX_COMPARE_PLAYERS = 5;
/** The colour letters (`.marker-a` … `.compare-chip-e`), in order. */
export const COMPARE_LETTERS = ["a", "b", "c", "d", "e"] as const;

/** One placed module, as the rules need it. */
export interface DropModule {
  /** The `.module-title` text, trimmed (undefined without one). */
  title: string | undefined;
  /** The `.player-select`'s player id; "" for a picker with no player (main.js's empty
   *  value, which some rules treat differently from no picker); null with no picker. */
  selectValue: PlayerId | "" | null;
  /** `_mergedPlayers` (ids) — set means it's a comparison. */
  mergedPlayers?: PlayerId[];
  mergedSeasons?: (string | null | undefined)[];
  /** `_selectedSeason` (the season picker's choice). */
  selectedSeason?: string;
  /** `_selectedSeasonPlayer`: whose season `selectedSeason` is (live: a pick is per player). */
  selectedSeasonPlayer?: PlayerId;
}

/** The modules.json fields the rules read. */
export interface DropConfig {
  renderer?: string;
  categoryId?: string;
  mergeable?: boolean;
}

export interface DropWorld {
  config: (title: string | undefined) => DropConfig | null;
  /** The snapshot's players (keyed by name; looked up by id through playerById). */
  players: Record<string, RawPlayerRecord>;
  /** The sticky last-picked player. */
  sticky: PlayerId | null;
  /** Live only (ids-3): picked players the snapshot doesn't have — pickers offer them too.
   *  None on the snapshot site. */
  extras?: readonly PickedPlayer[];
  /** Live only (T2-14): a player's gender, true = women's, once known (a live Stats / Radar tile
   *  has loaded her; a snapshot player is a woman), else null. Unset on the snapshot site. */
  womensOf?: (id: PlayerId) => boolean | null;
  /** Live only: is she a goalkeeper, once a live tile has noted it; else null. */
  keeperOf?: (id: PlayerId) => boolean | null;
  /** Live only: the season "her current one" (a null season) opened on in a live tile, else null
   *  (player-gender.ts), so a Bio's player and a tile's "26/27" count as the same season. */
  currentSeasonOf?: (id: PlayerId) => string | null;
}

/**
 * A player the snapshot doesn't have (a live pick, ids-3) is never dragged into another
 * module — fork 3A: a merge, a switch or an overlay reads her snapshot record (her seasons,
 * her position for Goalkeeping), which she doesn't have. The drop goes red, as any refused
 * drop does, until those widgets read real data (live Stats + Radar tiles do: `readsLivePlayers`; so
 * do the live maps: `isLiveMap`). Never
 * true on the snapshot site.
 */
function outsideSnapshot(id: PlayerId | "" | null | undefined, w: Pick<DropWorld, "extras">): boolean {
  // Exactly the live picks the snapshot lacks (`extras`) — so the snapshot site, where the
  // list is empty, decides every drop as it always did.
  return typeof id === "number" && !!w.extras?.some((p) => p.id === id);
}

/** Nor is anyone merged INTO a module showing such a player: the comparison would hold her. */
function showsOutsideSnapshot(m: DropModule, w: Pick<DropWorld, "extras">): boolean {
  return (m.mergedPlayers ?? [singlePlayerOf(m)]).some((id) => outsideSnapshot(id, w));
}

type Season = string | null | undefined;

const isFull = (m: DropModule) => !!m.mergedPlayers && m.mergedPlayers.length >= MAX_COMPARE_PLAYERS;

/** The widgets whose comparison reads every player's real numbers on ?live=1: Stats (T2-14),
 *  Radar (T2-16b). */
const LIVE_COMPARISON_RENDERERS = new Set(["renderStatsModule", "renderRadarModule"]);

/**
 * Live: a tile that reads every player's real numbers (Stats, Radar), so any player can join it —
 * a live pick too (fork 3A is for widgets still on the snapshot). Never a man with a woman
 * (Prateek, 2026-10-15: nobody mixes them).
 */
function readsLivePlayers(m: DropModule, w: DropWorld): boolean {
  return !!w.womensOf && LIVE_COMPARISON_RENDERERS.has(w.config(m.title)?.renderer ?? "");
}

/** Live: the season a tile shows `id` on — its own season pick, else null (her current one). */
export function liveSeasonOf(m: DropModule, id: PlayerId): string | null {
  return m.selectedSeasonPlayer === id ? (m.selectedSeason ?? null) : null;
}

/**
 * Can `id` (on `season`, null = her current one) join live tile `modB` (Stats or Radar): room
 * for one more; not already on it **on the same season** (Ben, 2026-10-16: the same player on
 * another season is a year-on-year comparison, as on the snapshot site); and her gender known
 * and the same as theirs. A gender not known yet (a tile still loading) is refused too — a
 * mixed tile is the one thing it must never make.
 */
function canJoinLiveTile(modB: DropModule, id: PlayerId | "" | null | undefined, season: Season, w: DropWorld): boolean {
  if (typeof id !== "number" || isFull(modB)) return false;
  const on = modB.mergedPlayers
    ? modB.mergedPlayers.map((p, i) => ({ id: p, season: modB.mergedSeasons?.[i] ?? null }))
    : typeof modB.selectValue === "number"
      ? [{ id: modB.selectValue, season: liveSeasonOf(modB, modB.selectValue) }]
      : [];
  // A null season is "her current one": the same as the season it resolves to, so a Bio (no
  // season) dropped on a tile showing her current season is her again, not a year-on-year (Ben,
  // 2026-11-02: a group drop added Declan Rice twice).
  const same = (p: PlayerId, a: Season, b: Season) => {
    const cur = w.currentSeasonOf?.(p) ?? null;
    return (a ?? cur) === (b ?? cur);
  };
  if (on.length === 0 || on.some((p) => p.id === id && same(id, p.season, season ?? null))) return false;
  const womens = w.womensOf!(id);
  return womens !== null && on.every((p) => w.womensOf!(p.id) === womens);
}

/** The season a player shows on a module: its picked season if the player has it, else
 *  the player's current one. null for an unknown player. */
export function seasonFor(m: Pick<DropModule, "selectedSeason">, id: PlayerId, w: Pick<DropWorld, "players">): Season {
  const p = playerById(w.players, id);
  if (!p) return null;
  return m.selectedSeason && p.seasons?.[m.selectedSeason] ? m.selectedSeason : p.currentSeason;
}

/** The one player a module stands for — null for a comparison (no single value speaks for
 *  it) or a module without a picker. An empty picker is "", not null (as main.js). */
export function singlePlayerOf(m: DropModule): PlayerId | "" | null {
  if (m.mergedPlayers) return null;
  return m.selectValue;
}

/** Does modB already show this exact player on this exact season? The one duplicate a
 *  merge never allows — the same player on ANOTHER season is fine (comparing someone
 *  with their own earlier season). */
export function moduleHasPlayerSeason(modB: DropModule, id: PlayerId | "" | null, season: Season, w: DropWorld): boolean {
  if (!id || season == null) return false;
  if (modB.mergedPlayers) {
    return modB.mergedPlayers.some((n, i) => n === id && !!modB.mergedSeasons && modB.mergedSeasons[i] === season);
  }
  const existing = singlePlayerOf(modB);
  return !!existing && existing === id && seasonFor(modB, existing, w) === season;
}

function sameKind(a: DropConfig | null, b: DropConfig | null): boolean {
  if (!a || !b || !a.mergeable || !b.mergeable) return false;
  return a.renderer === b.renderer && a.categoryId === b.categoryId;
}

/** Two placed modules can merge: the same mergeable kind (renderer + category, e.g. two
 *  "Appearance"), the source a single player (a comparison can only be the TARGET), room
 *  for one more, and not a duplicate player + season. */
export function isMergeCompatible(modA: DropModule, modB: DropModule, w: DropWorld): boolean {
  if (modA.mergedPlayers) return false;
  if (readsLivePlayers(modB, w)) {
    const id = singlePlayerOf(modA);
    const season = typeof id === "number" ? liveSeasonOf(modA, id) : null;
    return sameKind(w.config(modA.title), w.config(modB.title)) && canJoinLiveTile(modB, id, season, w);
  }
  if (showsOutsideSnapshot(modA, w) || showsOutsideSnapshot(modB, w)) return false;
  if (isFull(modB)) return false;
  if (!sameKind(w.config(modA.title), w.config(modB.title))) return false;
  const sourceId = singlePlayerOf(modA);
  if (sourceId && moduleHasPlayerSeason(modB, sourceId, seasonFor(modA, sourceId, w), w)) return false;
  return true;
}

/** The same check for a side-panel tile (no element yet, only its title). Its player is
 *  the one the new module would open on (the sticky pick, else the first by surname —
 *  createModule's own rule), on that player's current season. */
export function isMergeCompatibleTitle(title: string, modB: DropModule, w: DropWorld): boolean {
  if (!title) return false;
  if (isFull(modB)) return false;
  const cfgA = w.config(title);
  if (readsLivePlayers(modB, w)) {
    if (!sameKind(cfgA, w.config(modB.title))) return false;
    // A new tile opens on the player's current season (null).
    return canJoinLiveTile(modB, openingPlayer(offeredPlayerIds(w.players, cfgA?.categoryId, w.extras), null, w.sticky), null, w);
  }
  if (showsOutsideSnapshot(modB, w)) return false;
  if (!sameKind(cfgA, w.config(modB.title))) return false;
  const playerId = openingPlayer(offeredPlayerIds(w.players, cfgA?.categoryId, w.extras), null, w.sticky);
  if (outsideSnapshot(playerId, w)) return false;
  const p = playerId === undefined ? undefined : playerById(w.players, playerId);
  if (p && playerId !== undefined && moduleHasPlayerSeason(modB, playerId, p.currentSeason, w)) return false;
  return true;
}

/**
 * A bare player (a Ranking / Squad row, a legend chip) — or a placed module's one player —
 * dropped onto a Radar or Stats module joins / forms a comparison. `playerName` defaults to
 * the sticky pick (a bare token only offers that), `season` to the player's current one.
 * Goalkeeping only takes goalkeepers (its picker has no one else).
 */
export function isPlayerTokenTarget(modB: DropModule, playerId: PlayerId | "" | null, season: Season, w: DropWorld): boolean {
  if (isFull(modB)) return false;
  if (readsLivePlayers(modB, w)) {
    const id = playerId || w.sticky;
    // Goalkeeping takes goalkeepers only (Ben, 2026-10-16): a snapshot player by her record; a
    // live pick once a live tile or Bio has noted her position — not known yet → refused, as an
    // unknown gender is.
    const p = typeof id === "number" ? playerById(w.players, id) : undefined;
    if (w.config(modB.title)?.categoryId === "goalkeeping") {
      const keeper = p ? p.positionTemplate === "goalkeeper" : typeof id === "number" ? (w.keeperOf?.(id) ?? null) : null;
      if (keeper !== true) return false;
    }
    return canJoinLiveTile(modB, id, season, w);
  }
  if (showsOutsideSnapshot(modB, w)) return false;
  const cfgB = w.config(modB.title);
  if (!cfgB) return false;
  if (cfgB.renderer !== "renderRadarModule" && cfgB.renderer !== "renderStatsModule") return false;
  // `||`, not `??`: a placed source with an empty picker ("") offers the sticky pick too.
  const id = playerId || w.sticky;
  if (outsideSnapshot(id, w)) return false;
  const p = id ? playerById(w.players, id) : undefined;
  if (cfgB.categoryId === "goalkeeping" && (!p || p.positionTemplate !== "goalkeeper")) return false;
  const effectiveSeason = season != null ? season : p?.currentSeason;
  if (p && moduleHasPlayerSeason(modB, id, effectiveSeason, w)) return false;
  return true;
}

/** One player a group drop adds: which dragged module (its place in `sources`), who, and the
 *  season they bring (as a single drop of that module would). */
export interface GroupJoiner {
  index: number;
  id: PlayerId;
  season: Season;
}

/**
 * A selected group of modules dropped on a Radar or Stats tile showing players (Ben,
 * 2026-11-02): who joins. Each source in order brings its one player, on the season it shows,
 * and joins exactly as a single drop would (isPlayerTokenTarget: room, the same gender, a
 * goalkeeper for Goalkeeping, not the same player on the same season), counting those who
 * joined before it. So a player already on the target is skipped, the same player twice in the
 * group joins once, and it stops at five. Sources with no single player (a comparison, a team,
 * no picker) bring nobody.
 */
export function groupJoiners(target: DropModule, sources: readonly DropModule[], w: DropWorld): GroupJoiner[] {
  const first = singlePlayerOf(target);
  // A merge needs a player already on the target (mergeModules); an empty tile takes nobody.
  if (!target.mergedPlayers && typeof first !== "number") return [];
  const live = readsLivePlayers(target, w);
  const joined: GroupJoiner[] = [];
  let cur = target;
  sources.forEach((src, index) => {
    const id = singlePlayerOf(src);
    if (typeof id !== "number") return;
    const season = live ? liveSeasonOf(src, id) : seasonFor(src, id, w);
    if (!live && outsideSnapshot(id, w)) return;
    if (!isPlayerTokenTarget(cur, id, season, w)) return;
    joined.push({ index, id, season });
    // The target as it would be with them on it, so the next check counts them.
    const members = cur.mergedPlayers ?? [first as PlayerId];
    const seasons = cur.mergedSeasons ?? [live ? liveSeasonOf(cur, first as PlayerId) : seasonFor(cur, first as PlayerId, w)];
    cur = { ...cur, mergedPlayers: [...members, id], mergedSeasons: [...seasons, season] };
  });
  return joined;
}

/** Shot Map / Pass Map / Passing Profile / Heat Map show one player and have no
 *  comparison: a player dropped on one replaces it. Live: Matches too (Ben, 2026-10-19 — a season
 *  dragged from the Bio's career strip opens it on that season). */
export function isSwitchPlayerTarget(modB: DropModule, w: DropWorld): boolean {
  const r = w.config(modB.title)?.renderer;
  return (
    r === "renderShotMapModule" ||
    r === "renderPassMapModule" ||
    r === "renderPassingProfileModule" ||
    r === "renderPassingReportModule" ||
    r === "renderHeatMapModule" ||
    (!!w.womensOf && r === "renderMatchesModule")
  );
}

/** A live map (Shot / Pass / Heat Map, Passing Profile / Report, Matches): it reads any player's
 *  real events, so a live pick can switch onto it (Ben, 2026-11-01: a Bio with Ezri Konsa wouldn't
 *  drop on a Pass Map). */
function isLiveMap(m: DropModule, w: DropWorld): boolean {
  return !!w.womensOf && isSwitchPlayerTarget(m, w);
}

/** A live Similarity (reads any player's real numbers, so a live pick can be its overlay). */
function isLiveSimilarity(m: DropModule, w: DropWorld): boolean {
  return !!w.womensOf && w.config(m.title)?.renderer === "renderSimilarityModule";
}

/**
 * Similarity: a dropped player (`playerId`; a bare token offers the sticky pick) becomes its
 * overlay, never its base player. Live: she must be the base's gender, both known — a man is never
 * drawn over a woman (Prateek, 2026-10-15, as Stats / Radar) — and not the base herself. The
 * snapshot site decides as it always did (any player).
 */
export function isSimilarityOverlayTarget(modB: DropModule, playerId: PlayerId | "" | null, w: DropWorld): boolean {
  if (w.config(modB.title)?.renderer !== "renderSimilarityModule") return false;
  if (!isLiveSimilarity(modB, w)) return true;
  const id = playerId || w.sticky;
  const base = singlePlayerOf(modB);
  if (typeof id !== "number" || typeof base !== "number" || id === base) return false;
  const womens = w.womensOf!(id);
  return womens !== null && womens === w.womensOf!(base);
}

/** What dropping placed module A onto B does, or null (red — back home). */
export function resolveModuleDropAction(modA: DropModule, modB: DropModule, w: DropWorld): ModuleDropAction | null {
  if (outsideSnapshot(singlePlayerOf(modA), w) && !readsLivePlayers(modB, w) && !isLiveSimilarity(modB, w) && !isLiveMap(modB, w)) return null;
  if (isMergeCompatible(modA, modB, w)) return "merge";
  const playerId = singlePlayerOf(modA);
  if (playerId == null) return null;
  // Its real season goes through, so a same-player-same-season pair the merge check just
  // refused can't sneak back in here. (An empty picker, "", has no season of its own.)
  const season =
    playerId === "" ? null : readsLivePlayers(modB, w) ? liveSeasonOf(modA, playerId) : seasonFor(modA, playerId, w);
  if (isPlayerTokenTarget(modB, playerId, season, w)) return "merge";
  if (isSwitchPlayerTarget(modB, w)) return "switch";
  if (isSimilarityOverlayTarget(modB, playerId, w)) return "similarity-compare";
  return null;
}
